import { isEqual } from 'lodash';
import { Filter } from '@zapier/transfer';
import { ZapierSession } from '@zapier/universal-layout';

import { WSClient, FilteredRecord } from '../types';
import { TransferWebSocketError, TransferClientError } from '../../../errors';
import { Zap, ZDLStep } from '../../zapier/types';
import { flattenRecord } from '../../../utils';

type Filters = Filter.Criteria[];
type ReadConfig = Record<string, any>;

type Options = {
  accountId: ZapierSession['currentAccountId'];
  zapId: Zap['id'];
  readStep: ZDLStep;
  filterParams: Filters;
  onGetRecords: (record: FilteredRecord[]) => void;
  onError: (error: TransferWebSocketError) => void;
  onReadFailed: (data: TransferClientError) => void;
  onDone: () => void;
};

enum MessageType {
  SetReadConfig = 'SET_READ_CONFIG_V1',
  SetFilters = 'SET_FILTERS_V1',
  GetRecord = 'FILTERED_RECORD_V1',
  EndRead = 'END_READ_V1',
  ReadError = 'READ_ERROR_V1',
}

type Message = {
  type: MessageType;
} & Record<string, any>;

type Callbacks = {
  close: () => void;
  setFilters: (filters: Filters) => void;
};

type GetFilteredRecords = (client: WSClient, options: Options) => Callbacks;

export const getFilteredRecords: GetFilteredRecords = (
  client,
  {
    accountId,
    zapId,
    readStep,
    filterParams,
    onGetRecords,
    onError,
    onDone,
    onReadFailed,
  }
) => {
  const ws = client('v1/ws/filtered-records/');

  const readConfig: ReadConfig = {
    action: readStep.action,
    selectedAPI: readStep.app,
    params: readStep.params,
    typeOf: readStep.type,
    authId: readStep.authentication_id,
    accountId,
    zapId,
  };

  let currentFilters = filterParams;
  let records: FilteredRecord[] = [];

  const sendJSON = (message: Message) => ws.send(JSON.stringify(message));
  const setReadConfig = (rc: ReadConfig) =>
    sendJSON({ type: MessageType.SetReadConfig, read_config: rc });
  const setFilters = (filters: Filters) => {
    sendJSON({ type: MessageType.SetFilters, filters });
    currentFilters = filters;
    records = [];
  };
  const close = () => ws.close(1000);

  ws.onopen = () => {
    setReadConfig(readConfig);
    setFilters(filterParams);
  };

  ws.onmessage = (ev: MessageEvent) => {
    try {
      const parsed = JSON.parse(ev.data);
      switch (parsed.type) {
        case MessageType.GetRecord:
          // Ignore any message that was for a previous set of filters.
          if (isEqual(currentFilters, parsed.filters)) {
            records.push({
              isIncluded: parsed.included_by_filters,
              record: flattenRecord(parsed.record),
            });
            // if we return the original records array
            // then the ws connection can continue to modify
            // the array even after the listener may no longer want it modified
            // instead we return a new array
            onGetRecords([...records]);
          }
          break;
        case MessageType.ReadError:
          onReadFailed(new TransferClientError(parsed.error as string));
          break;
        case MessageType.EndRead:
          onDone();
          break;
      }
    } catch (e) {
      onError(new TransferWebSocketError(readConfig, ev));
    }
  };

  ws.onclose = (ev: CloseEvent) => {
    if (ev.code !== 1000) {
      onError(new TransferWebSocketError(readConfig, ev));
    }
    onDone();
  };

  ws.onerror = (ev) => {
    onError(new TransferWebSocketError(readConfig, ev));
  };

  return { setFilters, close };
};

export default getFilteredRecords;
